import { cliente } from '@/lib/cliente';
import type {
  ActualizarEnsayoEsclerometroRequest,
  ActualizarReboteRequest,
  CrearEnsayoEsclerometroRequest,
  CrearReboteRequest,
  EnsayoEsclerometroDetalle,
  ListaEnsayosEsclerometroResponse,
  ListarEnsayosEsclerometroRequest,
  Rebote,
  RespuestaApi,
} from '@adelante/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

// =============================================================================
// Esclerómetro — hooks de query + mutación.
//
// Mismo patrón que useLaboratorio: keys coercionadas a Number (BIGINT viene
// como string del driver), invalidación de listado + detalle al éxito de
// cualquier mutación que afecte el ensayo.
// =============================================================================

const keyEnsayo = (id: number | string) => ['lab', 'esclerometro', 'ensayo', Number(id)] as const;

export function useEnsayosEsclerometro(filtros: Partial<ListarEnsayosEsclerometroRequest>) {
  return useQuery<RespuestaApi<ListaEnsayosEsclerometroResponse>>({
    queryKey: ['lab', 'esclerometro', 'lista', filtros],
    queryFn: () => cliente.lab.listarEsclerometro(filtros),
  });
}

export function useEnsayoEsclerometro(id: number | null) {
  return useQuery<RespuestaApi<EnsayoEsclerometroDetalle>>({
    queryKey: ['lab', 'esclerometro', 'ensayo', id],
    queryFn: () => {
      if (id === null) throw new Error('id es null');
      return cliente.lab.obtenerEsclerometro(id);
    },
    enabled: id !== null,
  });
}

export function useCrearEnsayoEsclerometro() {
  const qc = useQueryClient();
  return useMutation<
    RespuestaApi<EnsayoEsclerometroDetalle>,
    Error,
    CrearEnsayoEsclerometroRequest
  >({
    mutationFn: (body) => cliente.lab.crearEsclerometro(body),
    onSuccess: (r) => {
      if (r.data) {
        toast.success(`Ensayo #${r.data.numero} creado`);
        qc.invalidateQueries({ queryKey: ['lab', 'esclerometro', 'lista'] });
        qc.setQueryData(keyEnsayo(r.data.id), r);
      } else {
        toast.error(r.error?.mensaje ?? 'Error desconocido');
      }
    },
    onError: (e) => toast.error('No se pudo crear el ensayo', { description: e.message }),
  });
}

export function useActualizarEnsayoEsclerometro() {
  const qc = useQueryClient();
  return useMutation<
    RespuestaApi<EnsayoEsclerometroDetalle>,
    Error,
    { id: number } & ActualizarEnsayoEsclerometroRequest
  >({
    mutationFn: ({ id, ...body }) => cliente.lab.actualizarEsclerometro(id, body),
    onSuccess: (r, vars) => {
      if (r.data) {
        toast.success('Ensayo actualizado');
        qc.setQueryData(keyEnsayo(vars.id), r);
        qc.invalidateQueries({ queryKey: ['lab', 'esclerometro', 'lista'] });
      } else {
        toast.error(r.error?.mensaje ?? 'Error desconocido');
      }
    },
    onError: (e) => toast.error('No se pudo actualizar el ensayo', { description: e.message }),
  });
}

export function useEliminarEnsayoEsclerometro() {
  const qc = useQueryClient();
  return useMutation<RespuestaApi<{ ok: true }>, Error, { id: number }>({
    mutationFn: ({ id }) => cliente.lab.eliminarEsclerometro(id),
    onSuccess: (r, vars) => {
      if (r.data) {
        toast.success('Ensayo eliminado');
        qc.removeQueries({ queryKey: keyEnsayo(vars.id) });
        qc.invalidateQueries({ queryKey: ['lab', 'esclerometro', 'lista'] });
      } else {
        toast.error(r.error?.mensaje ?? 'Error desconocido');
      }
    },
    onError: (e) => toast.error('No se pudo eliminar el ensayo', { description: e.message }),
  });
}

// ---- Rebotes ----

export function useCrearRebote() {
  const qc = useQueryClient();
  return useMutation<RespuestaApi<Rebote>, Error, { idEnsayo: number } & CrearReboteRequest>({
    mutationFn: ({ idEnsayo, ...body }) => cliente.lab.crearRebote(idEnsayo, body),
    onSuccess: (r, vars) => {
      if (r.data) {
        qc.invalidateQueries({ queryKey: keyEnsayo(vars.idEnsayo) });
        qc.invalidateQueries({ queryKey: ['lab', 'esclerometro', 'lista'] });
      } else {
        toast.error(r.error?.mensaje ?? 'Error desconocido');
      }
    },
    onError: (e) => toast.error('No se pudo agregar el rebote', { description: e.message }),
  });
}

export function useActualizarRebote() {
  const qc = useQueryClient();
  return useMutation<
    RespuestaApi<{ ok: true }>,
    Error,
    { id: number; idEnsayo: number } & ActualizarReboteRequest
  >({
    mutationFn: ({ id, idEnsayo: _, ...body }) => cliente.lab.actualizarRebote(id, body),
    onSuccess: (r, vars) => {
      if (r.data) {
        qc.invalidateQueries({ queryKey: keyEnsayo(vars.idEnsayo) });
        qc.invalidateQueries({ queryKey: ['lab', 'esclerometro', 'lista'] });
      } else {
        toast.error(r.error?.mensaje ?? 'Error desconocido');
      }
    },
    onError: (e) => toast.error('No se pudo actualizar el rebote', { description: e.message }),
  });
}

export function useEliminarRebote() {
  const qc = useQueryClient();
  return useMutation<RespuestaApi<{ ok: true }>, Error, { id: number; idEnsayo: number }>({
    mutationFn: ({ id }) => cliente.lab.eliminarRebote(id),
    onSuccess: (r, vars) => {
      if (r.data) {
        qc.invalidateQueries({ queryKey: keyEnsayo(vars.idEnsayo) });
        qc.invalidateQueries({ queryKey: ['lab', 'esclerometro', 'lista'] });
      } else {
        toast.error(r.error?.mensaje ?? 'Error desconocido');
      }
    },
    onError: (e) => toast.error('No se pudo eliminar el rebote', { description: e.message }),
  });
}
